import { canonicalJson, hash64Hex } from './hash.js';
import {
  BAGLOCK_RULES,
  CLEARGOAL_RULES,
  COLLECTGOAL_RULES,
  DISPENSER_RULES,
  GLUE_RULES,
  GRID_H,
  GRID_W,
  HIDDENLINE_RULES,
  Kind,
  LOCKKEY_RULES,
  MAX_BAG_LOCK,
  MAX_CUSTOMERS,
  MAX_GOLDEN,
  MAX_LOCKS,
  MAX_DISPENSERS,
  MAX_DISPENSER_ITEMS,
  MAX_LAYER,
  MAX_SHELF_ITEMS,
  MAX_SHELVES,
  MAX_TRAYS,
  MIN_DISPENSER_ITEMS,
  MIN_GOLDEN,
  MIN_LOCKS,
  MIN_SHELF_ITEMS,
  RULES_VERSION,
  SHELF_RULES,
  THREECUSTOMERS_RULES,
  TRAY_RULES,
  type LevelDispenser,
  type LevelShelf,
  type LevelTile,
  type LevelTray,
  type LevelV1,
} from './types.js';

/** Static, precomputed form of a level shared by every round of it. */
export interface CompiledLevel {
  level: LevelV1;
  hash: string;
  items: string[]; // item index -> item id
  itemIndex: Map<string, number>;
  n: number; // pieces: board tiles, then crates, then conveyor tiles, then dispenser items, then tray pieces, then shelf items
  nBoard: number;
  nCrates: number;
  nConv: number;
  /**
   * Dispensers (rules v2): dispenser d's items are pieces dispStart[d]..dispStart[d+1]-1, top
   * first, after the conveyor's (so levels without dispensers keep every piece index). Each item
   * sits at its machine's x, y, layer. The stack is spatial cover: an item is covered by every
   * item above it in its stack, by the tiles and crates over the machine, and it covers what the
   * machine stands on. So the top item is the only one that can be free, a tap takes it like a
   * tile, the next item's cover drops to what lies over the machine, and when the last item goes
   * the pieces below are released. It is the conveyor queue with one slot at a board cell, the
   * slot's "ahead" count kept in `cover` (the conveyor counts queue entries ahead instead).
   */
  nDisp: number;
  dispStart: Int16Array;
  /** dispenser index of piece i, -1 for every other piece */
  dispOf: Int8Array;
  /**
   * Trays (rules v2): tray k is the three pieces trayStart[k], +1, +2, after every dispenser item
   * (so every level without trays keeps every piece index; trayStart[nTrays] = n, and
   * trayStart[0] = dispStart[nDisp] is where trays would start on any level). All three sit at
   * the tray's x, y, layer with its item and are spatial cover like a tile: covered by the pieces
   * above that overlap the cell, covering what they overlap below, and never covering each other
   * (one layer). Only the first piece is a tap target: the tap pops all three (doTap).
   */
  nTrays: number;
  trayStart: Int16Array;
  /** tray index of piece i, -1 for every other piece */
  trayOf: Int8Array;
  /**
   * Shelves (rules v2): shelf s's items are pieces shelfStart[s]..shelfStart[s+1]-1, left to
   * right, after every tray piece (so every level without shelves keeps every piece index;
   * shelfStart[nShelves] = n, and shelfStart[0] = trayStart[nTrays] is where shelves would start
   * on any level). Like conveyor pieces they are not spatial (layer -1, no x/y, no cover either
   * way). A shelf piece is tappable while it is one of its shelf's ends: the first or the last
   * piece of the shelf still on the board (state.ts shelfEnds), so the row is a deque.
   */
  nShelves: number;
  /** shelf items (pieces) over every shelf, 0 on every level without shelves */
  nShelf: number;
  shelfStart: Int16Array;
  /** shelf index of piece i, -1 for every other piece */
  shelfOf: Int8Array;
  kind: Uint8Array;
  x: Int16Array;
  y: Int16Array;
  layer: Int8Array;
  item0: Int16Array; // -1 for crates
  wrapped: Uint8Array;
  ice0: Uint8Array;
  hp0: Uint8Array;
  convPos: Int16Array; // queue index for conveyor pieces, -1 otherwise
  convSlots: number;
  /** glue (rules v2): the other tile of i's glued pair, -1 when i is not glued */
  partner: Int16Array;
  /** number of glued pairs (0 on every level without glue: the rules-v1 paths run unchanged) */
  nGlue: number;
  /** bagLock (rules v2): slots locked at the start (0 on every level without it) */
  bagLock: number;
  /** lockKey (rules v2): item index of the key, -1 on every level without locks */
  lockKey: number;
  /** lockKey: 1 for a padlocked board tile, per piece */
  locked: Uint8Array;
  /** lockKey: the locked tiles, ascending (empty without locks: the rules-v1 paths run unchanged) */
  lockedTiles: number[];
  /**
   * clearGoal (rules v2): the round is won when every non-crate piece has popped, not when the
   * ticket is complete (false on every level without `goal`: the rules-v1 paths run unchanged)
   */
  clear: boolean;
  /**
   * collectGoal (rules v2): the round is won when the ticket is complete and every golden board
   * piece has popped (false on every level without `goal: 'collect'`).
   */
  collect: boolean;
  /** collectGoal: 1 for a golden board tile, per piece (all zero without the mechanic). */
  golden: Uint8Array;
  /** collectGoal: golden board pieces, ascending (empty without the mechanic). */
  goldenTiles: number[];
  /** clearGoal: pieces a full clear takes (every piece but the crates) */
  nItems: number;
  /**
   * hiddenLine (rules v2): the global index of the face-down line, -1 on every level without one
   * (the rules-v1 paths run unchanged). `revealLine` is its revealer: the line just before it on
   * the same customer's ticket (hiddenLine - 1), -1 without a hidden line.
   */
  hiddenLine: number;
  revealLine: number;
  /** GP-09 relabel swaps as item indices, in application order */
  assistSwaps: [number, number][];
  underStart: Int32Array; // CSR: pieces covered by i are underList[underStart[i]..underStart[i+1])
  underList: Int32Array;
  cover0: Uint8Array; // pieces covering i at start
  nLines: number;
  lineItem: Int16Array;
  lineCount0: Int16Array;
  lineCustomer: Int8Array;
  /** customers (ticket cards): 1..2, or 3 on a threeCustomers level (rules v2); serving is per line, in line order */
  nCustomers: number;
  orderSets0: number;
  bag: number;
  rushMs: number;
  par: number;
  rewardCoins: number;
}

export function levelHash(level: LevelV1): string {
  return hash64Hex(
    canonicalJson({
      v: level.v,
      rulesVersion: level.rulesVersion,
      grid: level.grid,
      tiles: level.tiles,
      crates: level.crates ?? [],
      conveyor: level.conveyor ?? null,
      // left out when absent (canonicalJson drops undefined), so levels without them keep their hash
      dispensers: level.dispensers,
      // left out when absent, like dispensers, so levels without trays keep their hash
      trays: level.trays,
      // left out when absent, like trays, so levels without shelves keep their hash
      shelves: level.shelves,
      // a line's `hidden` flag is part of its line (left out when absent, so levels without it keep their hash)
      customers: level.customers.map((c) => c.lines),
      bag: level.bag,
      // left out when absent (canonicalJson drops undefined), so levels without it keep their hash
      bagLock: level.bagLock,
      // left out when absent (a tile's `locked` flag is part of `tiles`), so levels without it keep their hash
      lockKey: level.lockKey,
      // left out when absent (the order goal), so levels without it keep their hash
      goal: level.goal,
      rush: level.rush ?? null,
      // par and reward feed the verified stars and coins: a changed value must invalidate stale copies
      par: level.par,
      reward: level.reward.coins,
      assist: level.assist ?? [],
      // stats, generator and lookalikes are left out: records and presentation, never play
    }),
  );
}

/**
 * Lookalike substitutes (presentation only, not hashed): at least one entry; every key a clutter
 * item of the level (in the pile, not on the ticket); every value an item the level does not hold,
 * so the substitute art never repeats one already on screen; values distinct, so two lookalikes
 * never turn into one look. Which items those are is the tooling's (levels-validate).
 */
function lookalikeErrors(lookalikes: Record<string, string>, totals: Map<string, number>, need: Map<string, number>): string[] {
  // a level read from JSON can carry any shape here (null, an array, a string): an error, never a throw
  if (typeof lookalikes !== 'object' || lookalikes === null || Array.isArray(lookalikes)) return ['lookalikes must be a map of clutter item to substitute item'];
  const errs: string[] = [];
  const entries = Object.entries(lookalikes);
  if (!entries.length) errs.push('lookalikes is empty (leave it out)');
  const seen = new Set<string>();
  for (const [item, sub] of entries) {
    if (!totals.has(item) || need.has(item)) errs.push(`lookalike ${item} is not a clutter item of the level`);
    if (typeof sub !== 'string' || !sub || totals.has(sub) || need.has(sub)) errs.push(`lookalike ${item}: substitute ${String(sub)} must be an item the level does not hold`);
    else if (seen.has(sub)) errs.push(`lookalike substitute ${sub} is used twice`);
    seen.add(sub);
  }
  return errs;
}

/** Glue adjacency: same layer is checked by the caller; the 2×2 footprints share a full side. */
export function glueAdjacent(a: { x: number; y: number }, b: { x: number; y: number }): boolean {
  return (a.y === b.y && Math.abs(a.x - b.x) === 2) || (a.x === b.x && Math.abs(a.y - b.y) === 2);
}

/** Glue problems of a tile list: every id on exactly two tiles, same layer, adjacent, no ice or wrap. */
function glueErrors(tiles: LevelTile[]): string[] {
  const errs: string[] = [];
  const byId = new Map<number, number[]>();
  tiles.forEach((t, i) => {
    if (t.glue === undefined) return;
    if (!Number.isInteger(t.glue) || t.glue < 1) errs.push(`tile ${i} bad glue id`);
    if (t.ice !== undefined || t.wrapped) errs.push(`tile ${i} is glued and has ice or wrap`);
    byId.set(t.glue, [...(byId.get(t.glue) ?? []), i]);
  });
  for (const [id, ix] of byId) {
    if (ix.length !== 2) {
      errs.push(`glue ${id} on ${ix.length} tiles (exactly 2)`);
      continue;
    }
    const a = tiles[ix[0]!]!;
    const b = tiles[ix[1]!]!;
    if (a.layer !== b.layer) errs.push(`glue ${id} tiles on different layers`);
    else if (!glueAdjacent(a, b)) errs.push(`glue ${id} tiles not orthogonally adjacent`);
  }
  return errs;
}

/** Dispenser problems: count, items, position and rules version (overlaps are checked with every piece). */
function dispenserErrors(level: LevelV1, w: number, h: number): string[] {
  const errs: string[] = [];
  const ds: LevelDispenser[] = level.dispensers ?? [];
  if (ds.length < 1 || ds.length > MAX_DISPENSERS) errs.push(`dispensers must be 1..${MAX_DISPENSERS}`);
  ds.forEach((d, k) => {
    if (!Number.isInteger(d.x) || !Number.isInteger(d.y) || d.x < 0 || d.y < 0 || d.x > w - 2 || d.y > h - 2) errs.push(`dispenser ${k} out of grid`);
    if (!Number.isInteger(d.layer) || d.layer < 0 || d.layer > MAX_LAYER) errs.push(`dispenser ${k} bad layer`);
    const n = d.items?.length ?? 0;
    if (n < MIN_DISPENSER_ITEMS || n > MAX_DISPENSER_ITEMS) errs.push(`dispenser ${k} holds ${n} items (${MIN_DISPENSER_ITEMS}..${MAX_DISPENSER_ITEMS})`);
    if (d.items?.some((it) => typeof it !== 'string' || !it)) errs.push(`dispenser ${k} bad item`);
    for (let j = 0; j < k; j++) if (overlaps(d.x, d.y, ds[j]!.x, ds[j]!.y)) errs.push(`dispensers ${j} and ${k} overlap`);
  });
  if (level.rulesVersion < DISPENSER_RULES) errs.push(`dispensers need rulesVersion ${DISPENSER_RULES}`);
  return errs;
}

/**
 * Tray problems: 1..MAX_TRAYS trays, each in the grid like a tile, on a layer 0..MAX_LAYER, with a
 * non-empty item, and rules v2. Overlaps are checked with every piece (a tray is one piece there);
 * its three items count in the item totals and the piece count (validateLevel).
 */
function trayErrors(level: LevelV1, w: number, h: number): string[] {
  const errs: string[] = [];
  const ts: LevelTray[] = level.trays ?? [];
  if (ts.length < 1 || ts.length > MAX_TRAYS) errs.push(`trays must be 1..${MAX_TRAYS}`);
  ts.forEach((t, k) => {
    if (!Number.isInteger(t.x) || !Number.isInteger(t.y) || t.x < 0 || t.y < 0 || t.x > w - 2 || t.y > h - 2) errs.push(`tray ${k} out of grid`);
    if (!Number.isInteger(t.layer) || t.layer < 0 || t.layer > MAX_LAYER) errs.push(`tray ${k} bad layer`);
    if (typeof t.item !== 'string' || !t.item) errs.push(`tray ${k} bad item`);
  });
  if (level.rulesVersion < TRAY_RULES) errs.push(`trays need rulesVersion ${TRAY_RULES}`);
  return errs;
}

/**
 * Shelf problems: 1..MAX_SHELVES shelves, each holding MIN..MAX_SHELF_ITEMS non-empty item
 * strings, and rules v2. A shelf is not on the board grid, so it has no position and no overlap
 * check (like the conveyor); its items count in the item totals and the piece count (validateLevel).
 */
function shelfErrors(level: LevelV1): string[] {
  const errs: string[] = [];
  const ss: LevelShelf[] = level.shelves ?? [];
  if (ss.length < 1 || ss.length > MAX_SHELVES) errs.push(`shelves must be 1..${MAX_SHELVES}`);
  ss.forEach((sh, k) => {
    const n = sh.items?.length ?? 0;
    if (n < MIN_SHELF_ITEMS || n > MAX_SHELF_ITEMS) errs.push(`shelf ${k} holds ${n} items (${MIN_SHELF_ITEMS}..${MAX_SHELF_ITEMS})`);
    if (sh.items?.some((it) => typeof it !== 'string' || !it)) errs.push(`shelf ${k} bad item`);
  });
  if (level.rulesVersion < SHELF_RULES) errs.push(`shelves need rulesVersion ${SHELF_RULES}`);
  return errs;
}

/**
 * Lock and key problems: MIN_LOCKS..MAX_LOCKS locked tiles, only with `lockKey` and vice versa, a
 * key that is an item of the pile, no locked tile of the key item, no glue, ice or wrap on a locked
 * tile, rules v2. (Assist swaps of the key are checked with the other swap rules.)
 */
function lockErrors(level: LevelV1, totals: Map<string, number>): string[] {
  const errs: string[] = [];
  const key = level.lockKey?.key;
  const locked: number[] = [];
  level.tiles.forEach((t, i) => {
    if (t.locked === undefined) return;
    if (t.locked !== true) errs.push(`tile ${i} bad locked flag`);
    locked.push(i);
    if (t.glue !== undefined || t.ice !== undefined || t.wrapped) errs.push(`tile ${i} is locked and has glue, ice or wrap`);
    if (key !== undefined && t.item === key) errs.push(`tile ${i} is locked and holds the key item`);
  });
  if (level.lockKey === undefined) {
    if (locked.length) errs.push('locked tiles need lockKey');
    return errs;
  }
  if (typeof key !== 'string' || !key) errs.push('lockKey needs a key item');
  else if (!totals.get(key)) errs.push(`key item ${key} is not in the level`);
  if (locked.length < MIN_LOCKS || locked.length > MAX_LOCKS) errs.push(`lockKey locks ${locked.length} tiles (${MIN_LOCKS}..${MAX_LOCKS})`);
  if (level.rulesVersion < LOCKKEY_RULES) errs.push(`lockKey needs rulesVersion ${LOCKKEY_RULES}`);
  return errs;
}

/**
 * Hidden line problems: at most one `hidden` line, flagged `true`, the last line of a customer
 * with at least 2 lines (so never the only line, and it always has a revealer), with an item on no
 * other line, rules v2, and no assist swap of its item.
 */
function hiddenLineErrors(level: LevelV1): string[] {
  const errs: string[] = [];
  const at: [number, number][] = [];
  level.customers.forEach((c, ci) =>
    c.lines.forEach((l, li) => {
      if (l.hidden === undefined) return;
      if (l.hidden !== true) errs.push(`customer ${ci} line ${li} bad hidden flag`);
      at.push([ci, li]);
    }),
  );
  if (at.length > 1) errs.push(`${at.length} hidden lines (at most 1)`);
  for (const [ci, li] of at) {
    const c = level.customers[ci]!;
    if (c.lines.length < 2) errs.push(`customer ${ci} line ${li} is hidden but is the customer's only line`);
    else if (li !== c.lines.length - 1) errs.push(`customer ${ci} line ${li} is hidden but is not the customer's last line`);
    const item = c.lines[li]!.item;
    if (level.customers.some((d, cj) => d.lines.some((l, lj) => (cj !== ci || lj !== li) && l.item === item))) errs.push(`hidden line item ${item} is on another line`);
    for (const [a, b] of level.assist ?? []) if (a === item || b === item) errs.push(`assist swap ${a}<->${b} swaps the hidden line's item`);
  }
  if (level.rulesVersion < HIDDENLINE_RULES) errs.push(`hidden line needs rulesVersion ${HIDDENLINE_RULES}`);
  return errs;
}

/**
 * Collect goal problems: golden markers only with `goal: 'collect'` and vice versa, MIN..MAX
 * markers, literal `true`, plain board tiles (no glue, ice, wrap or lock), and rules v2.
 */
function goldenErrors(level: LevelV1): string[] {
  const errs: string[] = [];
  const golden: number[] = [];
  level.tiles.forEach((t, i) => {
    if (t.golden === undefined) return;
    golden.push(i);
    if (t.golden !== true) errs.push(`tile ${i} bad golden flag`);
    if (t.glue !== undefined || t.ice !== undefined || t.wrapped || t.locked !== undefined)
      errs.push(`tile ${i} is golden and has glue, ice, wrap or lock`);
  });
  if (level.goal !== 'collect' && golden.length) errs.push('golden tiles need goal collect');
  if (level.goal === 'collect' && (golden.length < MIN_GOLDEN || golden.length > MAX_GOLDEN))
    errs.push(`collect goal needs ${MIN_GOLDEN}..${MAX_GOLDEN} golden tiles`);
  if ((level.goal === 'collect' || golden.length) && level.rulesVersion < COLLECTGOAL_RULES)
    errs.push(`collect goal needs rulesVersion ${COLLECTGOAL_RULES}`);
  return errs;
}

/** Open bag slots a locked bag keeps at the start: a round must still hold a pair plus room to pop it. */
export const MIN_LOCKED_CAP = 4;

export function overlaps(ax: number, ay: number, bx: number, by: number): boolean {
  return Math.abs(ax - bx) < 2 && Math.abs(ay - by) < 2;
}

/**
 * Structural validation. Returns a list of problems (empty = valid). A level that needs newer
 * rules than this engine plays (`rulesVersion` > RULES_VERSION) is invalid here: an old client
 * refuses it instead of playing it with rules it does not know.
 */
export function validateLevel(level: LevelV1): string[] {
  const errs: string[] = [];
  if (level.v !== 1) errs.push('v must be 1');
  if (!Number.isInteger(level.rulesVersion) || level.rulesVersion < 1) errs.push('rulesVersion must be an integer >= 1');
  else if (level.rulesVersion > RULES_VERSION) errs.push(`rulesVersion ${level.rulesVersion} needs a newer engine (this one plays up to ${RULES_VERSION})`);
  const w = level.grid?.w ?? 0;
  const h = level.grid?.h ?? 0;
  if (w !== GRID_W || h !== GRID_H) errs.push(`grid must be ${GRID_W}x${GRID_H}`);
  if (level.bag < 5 || level.bag > 9) errs.push('bag must be 5..9');
  if (level.bagLock !== undefined) {
    if (!Number.isInteger(level.bagLock) || level.bagLock < 1 || level.bagLock > MAX_BAG_LOCK) errs.push(`bagLock must be 1..${MAX_BAG_LOCK}`);
    else if (level.bag - level.bagLock < MIN_LOCKED_CAP) errs.push(`bagLock ${level.bagLock} leaves ${level.bag - level.bagLock} open slots (at least ${MIN_LOCKED_CAP})`);
    if (level.rulesVersion < BAGLOCK_RULES) errs.push(`bagLock needs rulesVersion ${BAGLOCK_RULES}`);
  }
  const totals = new Map<string, number>();
  const add = (item: string) => totals.set(item, (totals.get(item) ?? 0) + 1);
  level.tiles.forEach((t, i) => {
    if (!Number.isInteger(t.x) || !Number.isInteger(t.y) || t.x < 0 || t.y < 0 || t.x > w - 2 || t.y > h - 2)
      errs.push(`tile ${i} out of grid`);
    if (!Number.isInteger(t.layer) || t.layer < 0 || t.layer > MAX_LAYER) errs.push(`tile ${i} bad layer`);
    if (t.ice !== undefined && (t.ice < 1 || t.ice > 2)) errs.push(`tile ${i} bad ice`);
    add(t.item);
  });
  for (const c of level.crates ?? []) {
    if (c.hp < 1 || c.hp > 3) errs.push('crate hp must be 1..3');
    if (c.layer < 0 || c.layer > MAX_LAYER) errs.push('crate bad layer');
  }
  if (level.conveyor) {
    if (level.conveyor.slots < 1 || level.conveyor.slots > 4) errs.push('conveyor slots 1..4');
    level.conveyor.items.forEach(add);
  }
  if (level.dispensers !== undefined) {
    errs.push(...dispenserErrors(level, w, h));
    for (const d of level.dispensers) d.items?.forEach(add);
  }
  // a tray is three items of its type: they count for whole triples, the ticket and the piece count
  if (level.trays !== undefined) {
    errs.push(...trayErrors(level, w, h));
    for (const t of level.trays) for (let k = 0; k < 3; k++) add(t.item);
  }
  // shelf items are items of the pile like conveyor items (off the grid: no overlap check below)
  if (level.shelves !== undefined) {
    errs.push(...shelfErrors(level));
    for (const sh of level.shelves) sh.items?.forEach(add);
  }
  const n =
    level.tiles.length +
    (level.conveyor?.items.length ?? 0) +
    (level.dispensers ?? []).reduce((a, d) => a + (d.items?.length ?? 0), 0) +
    3 * (level.trays?.length ?? 0) +
    (level.shelves ?? []).reduce((a, sh) => a + (sh.items?.length ?? 0), 0);
  if (n < 9 || n > 120) errs.push(`tile count ${n} out of range`);
  for (const [item, count] of totals) if (count % 3 !== 0) errs.push(`item ${item} count ${count} not a multiple of 3`);
  if (level.customers.length < 1 || level.customers.length > MAX_CUSTOMERS) errs.push(`customers must be 1..${MAX_CUSTOMERS}`);
  // threeCustomers: a third ticket card is a rules-v2 mechanic (one or two customers stay v1)
  if (level.customers.length === 3 && level.rulesVersion < THREECUSTOMERS_RULES) errs.push(`three customers need rulesVersion ${THREECUSTOMERS_RULES}`);
  const need = new Map<string, number>();
  for (const c of level.customers) {
    if (c.lines.length < 1 || c.lines.length > 4) errs.push('ticket lines must be 1..4');
    for (const l of c.lines) {
      if (l.count < 3 || l.count > 9 || l.count % 3 !== 0) errs.push(`line ${l.item} count ${l.count} invalid`);
      need.set(l.item, (need.get(l.item) ?? 0) + l.count);
    }
  }
  for (const [item, count] of need)
    if ((totals.get(item) ?? 0) < count) errs.push(`order needs ${count} ${item}, level has ${totals.get(item) ?? 0}`);
  if (level.rush && (level.rush.ms < 10000 || level.rush.ms > 600000)) errs.push('rush ms out of range');
  // assist swaps: order item <-> non-order item, enough tiles to keep the ticket satisfiable
  const counts = new Map(totals);
  for (const [a, b] of level.assist ?? []) {
    const na = counts.get(a) ?? 0;
    const nb = counts.get(b) ?? 0;
    if (!need.has(a) || need.has(b) || na === 0 || nb === 0) errs.push(`assist swap ${a}<->${b} must pair an order item with a clutter item`);
    else if (nb < need.get(a)!) errs.push(`assist swap ${a}<->${b} leaves too few ${a} for the ticket`);
    counts.set(a, nb);
    counts.set(b, na);
  }
  if ((level.assist?.length ?? 0) > 2) errs.push('assist has at most 2 swaps');
  if (level.lookalikes !== undefined) errs.push(...lookalikeErrors(level.lookalikes, totals, need));
  if (level.lockKey !== undefined || level.tiles.some((t) => t.locked !== undefined)) {
    errs.push(...lockErrors(level, totals));
    // the key is an item type: a swap would move it onto other tiles (possibly locked ones)
    const key = level.lockKey?.key;
    for (const [a, b] of level.assist ?? []) if (key !== undefined && (a === key || b === key)) errs.push(`assist swap ${a}<->${b} swaps the key item`);
  }
  if (level.goal !== undefined) {
    // a full clear needs every item type in whole triples: already required of every level above
    if (level.goal !== 'clear' && level.goal !== 'collect')
      errs.push(`goal ${String(level.goal)} unknown (omit it for the order goal, or use 'clear' or 'collect')`);
    if (level.goal === 'clear' && level.rulesVersion < CLEARGOAL_RULES) errs.push(`clear goal needs rulesVersion ${CLEARGOAL_RULES}`);
    // relabelling ticket and clutter items cannot make a full clear easier: assist has no lever here
    if (level.goal === 'clear' && level.assist?.length) errs.push('a clear-goal level carries no assist swaps');
  }
  if (level.goal === 'collect' || level.tiles.some((t) => t.golden !== undefined)) errs.push(...goldenErrors(level));
  if (level.customers.some((c) => c.lines.some((l) => l.hidden !== undefined))) errs.push(...hiddenLineErrors(level));
  if (level.tiles.some((t) => t.glue !== undefined)) {
    errs.push(...glueErrors(level.tiles));
    if (level.rulesVersion < GLUE_RULES) errs.push(`glue needs rulesVersion ${GLUE_RULES}`);
  }
  // same position + same layer is a physical overlap
  const seen = new Set<string>();
  for (const t of level.tiles) {
    const k = `${t.x},${t.y},${t.layer}`;
    if (seen.has(k)) errs.push(`duplicate tile at ${k}`);
    seen.add(k);
  }
  // overlapping pieces must be on different layers (strict cover order); a dispenser or a tray is one piece here
  const pieces = [...level.tiles.map((t) => ({ x: t.x, y: t.y, layer: t.layer })), ...(level.crates ?? []), ...(level.dispensers ?? []), ...(level.trays ?? [])];
  for (let i = 0; i < pieces.length; i++)
    for (let j = i + 1; j < pieces.length; j++) {
      const a = pieces[i]!;
      const b = pieces[j]!;
      if (a.layer === b.layer && overlaps(a.x, a.y, b.x, b.y)) {
        errs.push(`pieces ${i} and ${j} overlap on layer ${a.layer}`);
        j = pieces.length;
        i = pieces.length;
      }
    }
  return errs;
}

export function compileLevel(level: LevelV1): CompiledLevel {
  const items: string[] = [];
  const itemIndex = new Map<string, number>();
  const idx = (id: string) => {
    let k = itemIndex.get(id);
    if (k === undefined) {
      k = items.length;
      items.push(id);
      itemIndex.set(id, k);
    }
    return k;
  };
  // order items first so indices are stable and readable
  for (const c of level.customers) for (const l of c.lines) idx(l.item);
  const nBoard = level.tiles.length;
  const crates = level.crates ?? [];
  const conv = level.conveyor?.items ?? [];
  const nCrates = crates.length;
  const nConv = conv.length;
  const disps = level.dispensers ?? [];
  const nDisp = disps.length;
  // absolute piece ranges: dispenser d holds pieces dispStart[d] .. dispStart[d + 1] - 1
  const dBase = nBoard + nCrates + nConv;
  const dispStart = new Int16Array(nDisp + 1).fill(dBase);
  for (let d = 0; d < nDisp; d++) dispStart[d + 1] = dispStart[d]! + disps[d]!.items.length;
  // trays after every dispenser item: tray k holds pieces trayStart[k] .. trayStart[k] + 2
  const trays = level.trays ?? [];
  const nTrays = trays.length;
  const trayStart = new Int16Array(nTrays + 1);
  for (let k = 0; k <= nTrays; k++) trayStart[k] = dispStart[nDisp]! + 3 * k;
  // shelves after every tray piece: shelf s holds pieces shelfStart[s] .. shelfStart[s + 1] - 1, left to right
  const shelves = level.shelves ?? [];
  const nShelves = shelves.length;
  const shelfStart = new Int16Array(nShelves + 1).fill(trayStart[nTrays]!);
  for (let k = 0; k < nShelves; k++) shelfStart[k + 1] = shelfStart[k]! + shelves[k]!.items.length;
  const n = shelfStart[nShelves]!;
  const kind = new Uint8Array(n);
  const x = new Int16Array(n);
  const y = new Int16Array(n);
  const layer = new Int8Array(n);
  const item0 = new Int16Array(n).fill(-1);
  const wrapped = new Uint8Array(n);
  const ice0 = new Uint8Array(n);
  const hp0 = new Uint8Array(n);
  const convPos = new Int16Array(n).fill(-1);
  const partner = new Int16Array(n).fill(-1);
  const golden = new Uint8Array(n);
  const goldenTiles: number[] = [];
  const dispOf = new Int8Array(n).fill(-1);
  const trayOf = new Int8Array(n).fill(-1);
  const shelfOf = new Int8Array(n).fill(-1);
  const glued = new Map<number, number>(); // glue id -> first tile seen
  let nGlue = 0;
  level.tiles.forEach((t, i) => {
    if (t.glue !== undefined) {
      const j = glued.get(t.glue);
      if (j === undefined) glued.set(t.glue, i);
      else {
        partner[i] = j;
        partner[j] = i;
        nGlue++;
      }
    }
    kind[i] = Kind.Tile;
    x[i] = t.x;
    y[i] = t.y;
    layer[i] = t.layer;
    item0[i] = idx(t.item);
    wrapped[i] = t.wrapped ? 1 : 0;
    ice0[i] = t.ice ?? 0;
    if (t.golden) {
      golden[i] = 1;
      goldenTiles.push(i);
    }
  });
  crates.forEach((c, j) => {
    const i = nBoard + j;
    kind[i] = Kind.Crate;
    x[i] = c.x;
    y[i] = c.y;
    layer[i] = c.layer;
    hp0[i] = c.hp;
  });
  conv.forEach((it, j) => {
    const i = nBoard + nCrates + j;
    kind[i] = Kind.Conveyor;
    item0[i] = idx(it);
    convPos[i] = j;
    layer[i] = -1;
  });
  disps.forEach((d, k) => {
    d.items.forEach((it, j) => {
      const i = dispStart[k]! + j;
      kind[i] = Kind.Dispenser;
      x[i] = d.x;
      y[i] = d.y;
      layer[i] = d.layer;
      item0[i] = idx(it);
      dispOf[i] = k;
    });
  });
  // item indices after the dispensers' (and before lockKey's), so levels without trays keep theirs
  trays.forEach((t, k) => {
    for (let j = 0; j < 3; j++) {
      const i = trayStart[k]! + j;
      kind[i] = Kind.Tray;
      x[i] = t.x;
      y[i] = t.y;
      layer[i] = t.layer;
      item0[i] = idx(t.item);
      trayOf[i] = k;
    }
  });
  // item indices after the trays' (and before lockKey's), so levels without shelves keep theirs;
  // off the grid like conveyor pieces (layer -1, x = y = 0, no cover)
  shelves.forEach((sh, k) => {
    sh.items.forEach((it, j) => {
      const i = shelfStart[k]! + j;
      kind[i] = Kind.Shelf;
      item0[i] = idx(it);
      layer[i] = -1;
      shelfOf[i] = k;
    });
  });
  // lockKey: the key's index is taken after every piece's, so item indices stay as without locks
  const lockKey = level.lockKey ? idx(level.lockKey.key) : -1;
  const locked = new Uint8Array(n);
  const lockedTiles: number[] = [];
  if (level.lockKey)
    level.tiles.forEach((t, i) => {
      if (t.locked) {
        locked[i] = 1;
        lockedTiles.push(i);
      }
    });
  // cover graph among spatial pieces (tiles + crates + dispenser items + tray pieces; conveyor
  // and shelf pieces have none). A tray's three pieces share one layer, so they never cover each other.
  const spatial = (i: number) => (i < nBoard + nCrates || i >= dBase) && i < shelfStart[0]!;
  const under: number[][] = Array.from({ length: n }, () => []);
  const cover0 = new Uint8Array(n);
  for (let a = 0; a < n; a++) {
    if (!spatial(a)) continue;
    for (let b = 0; b < n; b++) {
      if (!spatial(b)) continue;
      // one machine's items share its layer: the stack order is the cover order (top first)
      const over = dispOf[a]! >= 0 && dispOf[a] === dispOf[b] ? a < b : layer[a]! > layer[b]! && overlaps(x[a]!, y[a]!, x[b]!, y[b]!);
      if (over) {
        under[a]!.push(b);
        cover0[b]!++;
      }
    }
  }
  const underStart = new Int32Array(n + 1);
  let total = 0;
  for (let i = 0; i < n; i++) {
    underStart[i] = total;
    total += under[i]!.length;
  }
  underStart[n] = total;
  const underList = new Int32Array(total);
  for (let i = 0; i < n; i++) underList.set(under[i]!, underStart[i]!);

  const lines = level.customers.flatMap((c, ci) => c.lines.map((l) => ({ ci, l })));
  const lineItem = new Int16Array(lines.length);
  const lineCount0 = new Int16Array(lines.length);
  const lineCustomer = new Int8Array(lines.length);
  let orderSets0 = 0;
  let hiddenLine = -1;
  lines.forEach(({ ci, l }, i) => {
    if (l.hidden) hiddenLine = i;
    lineItem[i] = idx(l.item);
    lineCount0[i] = l.count;
    lineCustomer[i] = ci;
    orderSets0 += l.count / 3;
  });
  return {
    level,
    hash: levelHash(level),
    items,
    itemIndex,
    n,
    nBoard,
    nCrates,
    nConv,
    nDisp,
    dispStart,
    dispOf,
    nTrays,
    trayStart,
    trayOf,
    nShelves,
    nShelf: n - shelfStart[0]!,
    shelfStart,
    shelfOf,
    kind,
    x,
    y,
    layer,
    item0,
    wrapped,
    ice0,
    hp0,
    convPos,
    convSlots: level.conveyor?.slots ?? 0,
    partner,
    nGlue,
    bagLock: level.bagLock ?? 0,
    lockKey,
    locked,
    lockedTiles,
    clear: level.goal === 'clear',
    collect: level.goal === 'collect',
    golden,
    goldenTiles,
    nItems: n - nCrates,
    hiddenLine,
    revealLine: hiddenLine >= 0 ? hiddenLine - 1 : -1,
    assistSwaps: (level.assist ?? []).map(([a, b]) => [idx(a), idx(b)] as [number, number]),
    underStart,
    underList,
    cover0,
    nLines: lines.length,
    lineItem,
    lineCount0,
    lineCustomer,
    nCustomers: level.customers.length,
    orderSets0,
    bag: level.bag,
    rushMs: level.rush?.ms ?? 0,
    par: level.par,
    rewardCoins: level.reward.coins,
  };
}
